"use client";
import { useRouter, useParams } from "next/navigation";
import { EmailVerificationStep } from "@/components/onboarding/email-verification-step";
import { PlanSelectionStep } from "@/components/onboarding/plan-selection-step";
import { PaymentStep } from "@/components/onboarding/payment-step";
import { Progress } from "@radix-ui/react-progress";
import { useSession } from "@/lib/auth-client";
import { useTRPC } from "@/lib/trpc";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import type { OnboardingState } from "@/trpc/router/onboarding";
import { SignUpStep } from "@/components/onboarding/signup-step";

const STEP_MAP: Record<string, number> = {

  'signup': 0,
  'verify-email': 1,
  'choose-plan': 2,
  'make-payment': 3,
};

const STEP_NAMES = ["signup", "verify-email", "choose-plan", "make-payment"] as const;
const totalSteps = 4; // Excluding signin step

export default function OnboardingPage() {
  const router = useRouter();
  const params = useParams();
  const { data: session } = useSession();
  const trpc = useTRPC();
  
  const stepParam = (params?.step as string[])?.[0] || "signup";
  const currentStepIndex = STEP_MAP[stepParam] ?? 0;
  const currentStepName = STEP_NAMES[currentStepIndex] || "signup";

 

  const progress = currentStepIndex > 0 ? ((currentStepIndex / totalSteps) * 100) : 0;

  const navigateToStep = (stepName: string) => {
    router.push(`/create-account/${stepName}`);
  };

 

  const handleSignup = () => {
    navigateToStep("verify-email");
  };

  const handleEmailVerified = () => {
    navigateToStep("choose-plan");
  };

  const handlePlanSelected = (planId: string) => {
    router.push(`/create-account/make-payment?planId=${encodeURIComponent(planId)}`);
  };

  const handleFreePlanComplete = () => {
    router.replace("/dashboard");
  };

  const handlePaymentComplete = () => {
    router.replace("/dashboard");
  };

 

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {currentStepIndex > 0 && (
        <div className="border-b border-border bg-background sticky top-0 z-10">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center text-xs font-mono">
                PP
              </div>
              <div className="flex-1">
                <Progress value={progress} className="h-2" />
              </div>
              <span className="text-sm text-muted-foreground font-mono">
                Step {currentStepIndex} of {totalSteps}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 container mx-auto px-4 py-12 flex items-center justify-center h-full">
     
        {currentStepName === "signup" && (
          <SignUpStep onSignUp={handleSignup} />
        )}
        {currentStepName === "verify-email" && (
          <EmailVerificationStep
            email={session?.user?.email || ""}
            onNext={handleEmailVerified}
          />
        )}
        {currentStepName === "choose-plan" && (
          <PlanSelectionStep
            onNext={handlePlanSelected}
            onFreePlanComplete={handleFreePlanComplete}
          />
        )}
        {currentStepName === "make-payment" && (
          <PaymentStep
            planId={new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '').get("planId") || ""}
            onComplete={handlePaymentComplete}
          />
        )}
      </div>
    </div>
  );
}

